// Database Engine Types
export enum DatabaseEngine {
  POSTGRESQL = 'postgresql',
  MYSQL = 'pxc',
  MONGODB = 'psmdb',
}

// Database Cluster
export interface DatabaseCluster {
  metadata: {
    name: string;
    namespace: string;
    creationTimestamp?: string;
    uid?: string;
  };
  spec: {
    engine: {
      type: DatabaseEngine;
      version: string;
      replicas: number;
      resources?: {
        cpu: string;
        memory: string;
      };
      storage?: {
        class?: string;
        size: string;
      };
      config?: string;
    };
    proxy?: {
      type: 'haproxy' | 'proxysql' | 'mongos';
      replicas: number;
      expose?: {
        type: 'internal' | 'external';
      };
    };
    backup?: {
      enabled: boolean;
      schedules?: BackupSchedule[];
    };
    monitoring?: {
      enabled: boolean;
      monitoringConfigName?: string;
    };
  };
  status?: {
    status:
      | 'initializing'
      | 'provisioning'
      | 'ready'
      | 'error'
      | 'stopping'
      | 'stopped'
      | 'paused';
    message?: string;
    hostname?: string;
    port?: number;
    ready?: number;
    size?: number;
    activeStorage?: string;
  };
}

// Backup Schedule
export interface BackupSchedule {
  name: string;
  enabled: boolean;
  schedule: string; // Cron expression
  backupStorageName: string;
  retentionCopies?: number;
}

// Backup
export interface Backup {
  metadata: {
    name: string;
    namespace: string;
    creationTimestamp?: string;
  };
  spec: {
    dbClusterName: string;
    backupStorageName: string;
  };
  status?: {
    state: 'starting' | 'running' | 'succeeded' | 'failed';
    completed?: string;
    destination?: string;
    size?: number;
  };
}

// Storage Location
export interface StorageLocation {
  metadata: {
    name: string;
    namespace?: string;
  };
  spec: {
    type: 's3' | 'azure' | 'gcs';
    bucketName: string;
    region?: string;
    url?: string;
    verifyTLS?: boolean;
    credentials?: {
      accessKeyId?: string;
      secretAccessKey?: string;
      accountName?: string;
      accountKey?: string;
      credentialsFilePath?: string;
    };
  };
  status?: {
    usedBy?: string[];
  };
}

// Monitoring Endpoint
export interface MonitoringEndpoint {
  metadata: {
    name: string;
    namespace?: string;
  };
  spec: {
    type: 'pmm';
    url: string;
    allowedNamespaces?: string[];
    verifyTLS?: boolean;
    credentials?: {
      username?: string;
      password?: string;
    };
  };
  status?: {
    usedBy?: string[];
  };
}

// Database Engine Version
export interface DatabaseEngineVersion {
  engine: DatabaseEngine;
  version: string;
  status: 'recommended' | 'available' | 'deprecated';
  matrix?: {
    operator?: string;
    backup?: string;
  };
}

// Kubernetes Namespace Info
export interface NamespaceInfo {
  name: string;
  status: 'active' | 'terminating';
  operators?: {
    mongodb?: boolean;
    postgresql?: boolean;
    mysql?: boolean;
  };
}

// Create Database Request
export interface CreateDatabaseRequest {
  name: string;
  namespace: string;
  engine: DatabaseEngine;
  version: string;
  replicas: number;
  cpu: number;
  memory: number;
  storageClass?: string;
  storageSize: number;
  enableBackups?: boolean;
  backupSchedule?: string;
  backupStorage?: string;
  enableMonitoring?: boolean;
  monitoringEndpoint?: string;
  exposureType?: 'internal' | 'external';
}

// Database Component (for Components view)
export interface DatabaseComponent {
  name: string;
  type: 'database' | 'proxy' | 'backup-agent' | 'pmm-client';
  status: 'running' | 'pending' | 'error' | 'terminating';
  restarts: number;
  age?: string;
  node?: string;
  message?: string;
}

// Restore Request
export interface RestoreRequest {
  dbClusterName: string;
  namespace: string;
  backupName: string;
  dataSource?: {
    dbClusterBackupName?: string;
    pitr?: {
      type: 'latest' | 'date';
      date?: string;
    };
  };
}

// System Configuration
export interface EverestConfig {
  version: string;
  k8sVersion: string;
  namespaces: string[];
  operators: {
    mongodb?: {
      version: string;
      status: 'installed' | 'pending' | 'error';
    };
    postgresql?: {
      version: string;
      status: 'installed' | 'pending' | 'error';
    };
    mysql?: {
      version: string;
      status: 'installed' | 'pending' | 'error';
    };
  };
}
